import { useState, useEffect, useCallback, useRef } from 'react';
import { Modal } from 'react-bootstrap-v5';
import ArquivoRetornoService, { ContaRelatorioResumido, FiltrosBook } from '../../services/ArquivoRetornoService';

interface Props {
    esteiraID: number;
    show: boolean;
    onConfirm: (filtros: FiltrosBook, contasSelecionadas: number[]) => void;
    onCancel: () => void;
}

const FILTROS_DISPONIVEIS: { key: keyof FiltrosBook; label: string }[] = [
    { key: 'isFornecedor', label: 'Cliente e Fornecedor' },
    { key: 'isTributo', label: 'Tributos' },
    { key: 'isRetido', label: 'Retidos' },
    { key: 'isBanco', label: 'Banco' },
    { key: 'isSalario', label: 'Salário' },
    { key: 'isImobilizado', label: 'Imobilizado' },
    { key: 'isEstoque', label: 'Estoque' },
    { key: 'isFolha', label: 'Folha' },
    { key: 'isCapitalSocial', label: 'Capital Social' },
    { key: 'isEmprestimo', label: 'Empréstimo' },
];

const PAGE_SIZE = 30;

function ModalFiltrosBook({ esteiraID, show, onConfirm, onCancel }: Props) {
    const [filtros, setFiltros] = useState<FiltrosBook>({});
    const [contasSelecionadas, setContasSelecionadas] = useState<number[]>([]);

    // Multiselect de contas
    const [contas, setContas] = useState<ContaRelatorioResumido[]>([]);
    const [contasOffset, setContasOffset] = useState(0);
    const [temMaisContas, setTemMaisContas] = useState(true);
    const [loadingContas, setLoadingContas] = useState(false);
    const [queryContas, setQueryContas] = useState('');
    const [dropdownAberto, setDropdownAberto] = useState(false);

    const listRef = useRef<HTMLDivElement>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Reset ao abrir
    useEffect(() => {
        if (show) {
            setFiltros({});
            setContasSelecionadas([]);
            setContas([]);
            setContasOffset(0);
            setTemMaisContas(true);
            setQueryContas('');
            setDropdownAberto(false);
        }
    }, [show]);

    const carregarContas = useCallback(async (offset: number, query: string, append: boolean) => {
        if (!esteiraID) return;
        setLoadingContas(true);
        try {
            const result = await ArquivoRetornoService.listarContas(esteiraID, offset, PAGE_SIZE, query || undefined);
            if (append) {
                setContas(prev => [...prev, ...result]);
            } else {
                setContas(result);
            }
            setTemMaisContas(result.length === PAGE_SIZE);
            setContasOffset(offset + result.length);
        } catch {
            // silencioso
        } finally {
            setLoadingContas(false);
        }
    }, [esteiraID]);

    // Carregar contas ao abrir o dropdown
    useEffect(() => {
        if (dropdownAberto && contas.length === 0 && !loadingContas) {
            carregarContas(0, queryContas, false);
        }
    }, [dropdownAberto]);

    // Debounce na busca
    const handleQueryChange = (value: string) => {
        setQueryContas(value);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setContas([]);
            setContasOffset(0);
            setTemMaisContas(true);
            carregarContas(0, value, false);
        }, 400);
    };

    // Scroll infinito
    const handleScroll = () => {
        const el = listRef.current;
        if (!el || loadingContas || !temMaisContas) return;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
            carregarContas(contasOffset, queryContas, true);
        }
    };

    const toggleFiltro = (key: keyof FiltrosBook) => {
        setFiltros(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const toggleConta = (id: number) => {
        setContasSelecionadas(prev =>
            prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
        );
    };

    const removerConta = (id: number) => {
        setContasSelecionadas(prev => prev.filter(c => c !== id));
    };

    // Encontrar a conta pelo ID para exibir label
    const getContaLabel = (id: number): string => {
        const conta = contas.find(c => c.id === id);
        if (conta) return `${conta.contaEmpresa} - ${conta.descricaoEmpresa || conta.contaReferencial}`;
        return `Conta #${id}`;
    };

    const handleConfirm = () => {
        onConfirm(filtros, contasSelecionadas);
    };

    const nenhumFiltroSelecionado = !Object.values(filtros).some(v => v === true) && contasSelecionadas.length === 0;

    return (
        <Modal size='lg' centered show={show} onHide={onCancel} style={{ zIndex: 1060 }}>
            <div className='modal-content' style={{ backgroundColor: '#F4F5F7' }}>
                <div className='modal-header'>
                    <h5 className='modal-title titulo-modal'>Filtros do Book</h5>
                    <div className='btn btn-icon btn-sm btn-active-light-primary ms-2'>
                        <i onClick={onCancel} className='fas fa-times' style={{ color: '#5E6397', cursor: 'pointer' }} />
                    </div>
                </div>

                <div className='modal-body'>
                    {/* Filtros de tipo (flags do Evento) */}
                    <div className='mb-4'>
                        <label className='fw-bold mb-2 d-block' style={{ fontSize: 13, color: '#3F4254' }}>
                            Tipos de Conta
                        </label>
                        <p className='text-muted mb-3' style={{ fontSize: 12 }}>
                            Selecione os tipos de conta para incluir no Book. Deixe vazio para incluir todas.
                        </p>
                        <div className='row g-2'>
                            {FILTROS_DISPONIVEIS.map(f => (
                                <div className='col-6 col-md-4' key={f.key}>
                                    <label
                                        className='d-flex align-items-center gap-2 p-2 rounded cursor-pointer'
                                        style={{
                                            backgroundColor: filtros[f.key] ? '#e8f0fe' : '#fff',
                                            border: `1px solid ${filtros[f.key] ? '#4285f4' : '#e4e6ef'}`,
                                            cursor: 'pointer',
                                            transition: 'all 0.15s ease',
                                            fontSize: 12,
                                        }}
                                    >
                                        <input
                                            type='checkbox'
                                            checked={!!filtros[f.key]}
                                            onChange={() => toggleFiltro(f.key)}
                                            className='form-check-input m-0'
                                            style={{ width: 16, height: 16, cursor: 'pointer' }}
                                        />
                                        <span className='fw-semibold'>{f.label}</span>
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Contas sem movimentação */}
                    <div className='mb-4'>
                        <label
                            className='d-flex align-items-center gap-2 p-2 rounded cursor-pointer'
                            style={{
                                backgroundColor: filtros.ignorarContasSemMovimentacao ? '#e8f0fe' : '#fff',
                                border: `1px solid ${filtros.ignorarContasSemMovimentacao ? '#4285f4' : '#e4e6ef'}`,
                                cursor: 'pointer',
                                transition: 'all 0.15s ease',
                                fontSize: 12,
                            }}
                        >
                            <input
                                type='checkbox'
                                checked={!!filtros.ignorarContasSemMovimentacao}
                                onChange={() => toggleFiltro('ignorarContasSemMovimentacao')}
                                className='form-check-input m-0'
                                style={{ width: 16, height: 16, cursor: 'pointer' }}
                            />
                            <span className='fw-semibold'>Não gerar contas sem movimentação</span>
                        </label>
                    </div>

                    {/* Multiselect de contas */}
                    <div className='mb-3'>
                        <label className='fw-bold mb-2 d-block' style={{ fontSize: 13, color: '#3F4254' }}>
                            Contas Específicas <span className='text-muted fw-normal'>(opcional)</span>
                        </label>
                        <p className='text-muted mb-2' style={{ fontSize: 12 }}>
                            Selecione contas específicas para incluir no Book, ou deixe vazio para incluir todas.
                        </p>

                        {/* Tags de contas selecionadas */}
                        {contasSelecionadas.length > 0 && (
                            <div className='d-flex flex-wrap gap-1 mb-2'>
                                {contasSelecionadas.map(id => (
                                    <span
                                        key={id}
                                        className='badge d-flex align-items-center gap-1'
                                        style={{
                                            backgroundColor: '#e8f0fe',
                                            color: '#1a73e8',
                                            fontWeight: 500,
                                            fontSize: 11,
                                            padding: '4px 8px',
                                            borderRadius: 4,
                                        }}
                                    >
                                        {getContaLabel(id)}
                                        <i
                                            className='fas fa-times'
                                            style={{ cursor: 'pointer', fontSize: 9, marginLeft: 4 }}
                                            onClick={() => removerConta(id)}
                                        />
                                    </span>
                                ))}
                            </div>
                        )}

                        {/* Campo de busca + dropdown */}
                        <div style={{ position: 'relative' }}>
                            <input
                                type='text'
                                className='form-control form-control-sm'
                                placeholder='Buscar por conta ou descrição...'
                                value={queryContas}
                                onChange={e => handleQueryChange(e.target.value)}
                                onFocus={() => setDropdownAberto(true)}
                                style={{ fontSize: 12 }}
                            />
                            {dropdownAberto && (
                                <>
                                    <div
                                        style={{ position: 'fixed', inset: 0, zIndex: 1 }}
                                        onClick={() => setDropdownAberto(false)}
                                    />
                                    <div
                                        ref={listRef}
                                        onScroll={handleScroll}
                                        style={{
                                            position: 'absolute',
                                            top: '100%',
                                            left: 0,
                                            right: 0,
                                            maxHeight: 220,
                                            overflowY: 'auto',
                                            backgroundColor: '#fff',
                                            border: '1px solid #e4e6ef',
                                            borderTop: 'none',
                                            borderRadius: '0 0 6px 6px',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                            zIndex: 2,
                                        }}
                                    >
                                        {contas.length === 0 && !loadingContas && (
                                            <div style={{ padding: '12px 16px', color: '#6c757d', fontSize: 12, textAlign: 'center' }}>
                                                Nenhuma conta encontrada.
                                            </div>
                                        )}
                                        {contas.map(conta => {
                                            const selecionado = contasSelecionadas.includes(conta.id);
                                            return (
                                                <div
                                                    key={conta.id}
                                                    onClick={() => toggleConta(conta.id)}
                                                    style={{
                                                        padding: '8px 12px',
                                                        cursor: 'pointer',
                                                        backgroundColor: selecionado ? '#e8f0fe' : 'transparent',
                                                        borderBottom: '1px solid #f5f5f5',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 8,
                                                        transition: 'background-color 0.1s',
                                                    }}
                                                    onMouseEnter={e => {
                                                        if (!selecionado) (e.currentTarget as HTMLDivElement).style.backgroundColor = '#f8f9fa';
                                                    }}
                                                    onMouseLeave={e => {
                                                        (e.currentTarget as HTMLDivElement).style.backgroundColor = selecionado ? '#e8f0fe' : 'transparent';
                                                    }}
                                                >
                                                    <input
                                                        type='checkbox'
                                                        checked={selecionado}
                                                        readOnly
                                                        className='form-check-input m-0'
                                                        style={{ width: 14, height: 14, cursor: 'pointer', flexShrink: 0 }}
                                                    />
                                                    <div style={{ minWidth: 0, flex: 1 }}>
                                                        <div style={{ fontSize: 12, fontWeight: 600, color: '#3F4254' }}>
                                                            {conta.contaEmpresa}
                                                        </div>
                                                        <div style={{ fontSize: 11, color: '#A1A5B7', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {conta.descricaoEmpresa || conta.contaReferencial}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        {loadingContas && (
                                            <div style={{ padding: '10px', textAlign: 'center' }}>
                                                <span className='spinner-border spinner-border-sm text-primary' />
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Resumo */}
                    {nenhumFiltroSelecionado && (
                        <div className='alert alert-light d-flex align-items-center gap-2 py-2 px-3 mb-0' style={{ fontSize: 12 }}>
                            <i className='fas fa-info-circle text-primary' />
                            <span>Nenhum filtro selecionado — o Book será gerado com <strong>todas as contas</strong>.</span>
                        </div>
                    )}
                </div>

                <div className='modal-footer'>
                    <button className='btn btn-sm btn-light' onClick={onCancel}>
                        Cancelar
                    </button>
                    <button className='btn btn-sm btn-primary d-flex align-items-center gap-2' onClick={handleConfirm}>
                        <i className='fas fa-file-pdf' />
                        Gerar Book
                    </button>
                </div>
            </div>
        </Modal>
    );
}

export default ModalFiltrosBook;
